import { useEffect, useMemo, useRef, useState } from "react";
import * as api from "./v6Api";
import { useDismiss } from "../v6common/a11y";
import { phoneFmt, statusLabel, groupByTeam } from "./view";

// 전화 문의 등록(계약 §4-5). 사업자번호 10자리가 차면 바로 조회하고, 등록 뒤 사진은 이어서 올린다.
const bizDisplay = (d) => (d.length <= 3 ? d : d.length <= 5 ? `${d.slice(0, 3)}-${d.slice(3)}` : `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`);

export default function PhoneInquiryModal({ meta, staff, adminId, onClose, onCreated, onOpenExisting }) {
    const cats = meta?.categories || [];
    const [biz, setBiz] = useState(""); // 숫자만
    const [f, setF] = useState({ name: "", phone: "", category: cats[0]?.code || "", sub: cats[0]?.subcategories?.[0]?.code || "",
        status: "new", request: "", date: "", time: "" });
    const [assignees, setAssignees] = useState([]);
    const [reason, setReason] = useState(""); // 보류로 등록할 때의 사유(선택, 가맹점주에게 보인다)
    const [files, setFiles] = useState([]);
    const [res, setRes] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const lookupFor = useRef("");
    const set = (patch) => setF((p) => ({ ...p, ...patch }));
    const dismiss = useDismiss(onClose);
    const maxFiles = meta?.limits?.attachments_per_inquiry ?? 10;
    const cat = cats.find((c) => c.code === f.category);

    const previews = useMemo(() => files.map((file) => URL.createObjectURL(file)), [files]);
    useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

    const changeBiz = async (value) => {
        const d = value.replace(/\D/g, "").slice(0, 10);
        setBiz(d);
        if (res?.found) set({ name: "", phone: "" }); // 조회로 채운 값은 번호가 바뀌면 지운다
        setRes(null);
        lookupFor.current = d;
        if (d.length !== 10) return;
        try {
            const r = await api.lookupStore(d);
            if (lookupFor.current !== d) return; // 그 사이 번호가 바뀜
            setRes(r);
            if (r.found && r.store) set({ name: r.store.name || "", phone: phoneFmt(r.store.phone) });
        } catch (e) {
            if (lookupFor.current === d) setRes({ found: false, lookup_unavailable: true, store: null, open_inquiries: [], error: api.errorText(e) });
        }
    };

    const submit = async () => {
        const miss = [];
        if (biz.length !== 10) miss.push("사업자번호 10자리");
        if (!f.name.trim()) miss.push("상호명");
        if (!f.request.trim()) miss.push("요청 내용");
        if (miss.length) { setErr(`${miss.join(", ")}을(를) 입력해 주세요.`); return; }
        if (f.time && !f.date) { setErr("방문 날짜를 함께 골라 주세요."); return; }
        setErr("");
        setBusy(true);
        let created;
        try {
            created = await api.createPhone({
                business_number: biz, business_name: f.name.trim(), phone: f.phone.trim(),
                category_code: f.category, subcategory_code: f.sub, status: f.status, request_content: f.request.trim(),
                assignee_ids: assignees, schedule: f.date ? { date: f.date, time: f.time || null } : null, // 서버는 빈 문자열 시간을 거절한다
                ...(f.status === "on_hold" && reason.trim() ? { status_reason: reason.trim() } : {}),
            });
        } catch (e) {
            setErr(api.errorText(e));
            setBusy(false);
            return;
        }
        // 문의는 이미 만들어졌다. 사진 실패는 등록 성공으로 넘기고 경고만 붙인다(다시 등록하면 중복).
        let detail = created, warning;
        if (files.length) {
            try { detail = await api.addAttachments(created.id, files); }
            catch { warning = `No.${created.id} 등록은 됐지만 사진 올리기에 실패했습니다. 상세에서 다시 올려 주세요.`; }
        }
        setBusy(false);
        onCreated(detail, warning);
    };

    const selectable = groupByTeam(staff, { activeOnly: true });
    const dup = res?.open_inquiries || [];

    return (
        <div className="modal on" {...dismiss}>
            <div className="mbox" onClick={(e) => e.stopPropagation()}>
                <div className="mh"><h3>전화 문의 등록</h3><span style={{ color: "var(--tx3)", fontSize: 12.5 }}>통화하면서 바로 입력하세요</span>
                    <button className="btn btn-ghost btn-sm x" onClick={onClose} aria-label="닫기"><i className="fa-solid fa-xmark"></i></button></div>
                <div className="mb">
                    <div className="lookup">
                        <div className="fld"><label htmlFor="inq-n-biz">사업자번호 <span className="req">*</span> <span style={{ color: "var(--tx3)", fontWeight: 400 }}>(10자리를 넣으면 스토라넷에서 상호·연락처를 불러옵니다)</span></label></div>
                        <div className="row">
                            <input id="inq-n-biz" className="inp" placeholder="예: 123-45-67890" inputMode="numeric" autoComplete="off"
                                value={bizDisplay(biz)} onChange={(e) => changeBiz(e.target.value)} />
                        </div>
                        <div className={`res ${res ? "on" : ""}`}>
                            {res?.found && !res.lookup_unavailable && (
                                <div className="found"><span className="ok"><i className="fa-solid fa-circle-check"></i>{res.source === "local" ? "저장된 매장 정보" : "스토라넷 조회 완료"}</span>
                                    <b>{res.store?.name}</b><span style={{ color: "var(--tx2)" }}>{phoneFmt(res.store?.phone)}</span></div>
                            )}
                            {res && !res.found && !res.lookup_unavailable && (
                                <div style={{ color: "#92400e", fontSize: 12.5 }}><i className="fa-solid fa-circle-exclamation"></i> 스토라넷에 없는 사업자번호입니다. 상호와 연락처를 직접 입력해 주세요.</div>
                            )}
                            {res?.lookup_unavailable && (
                                <div style={{ color: "#92400e", fontSize: 12.5 }}><i className="fa-solid fa-circle-exclamation"></i> 스토라넷 조회가 안 됩니다. {res.found ? "저장된 매장 정보로 채웠어요. 확인해 주세요." : "상호와 연락처를 직접 입력해 주세요."}</div>
                            )}
                            {dup.length > 0 && (
                                <div className="dup"><i className="fa-solid fa-triangle-exclamation"></i> 이 매장에 아직 끝나지 않은 문의가 {dup.length}건 있어요:{" "}
                                    {dup.map((q, i) => (
                                        <span key={q.id}>{i > 0 && ", "}<a href={`#inquiry-${q.id}`} onClick={(e) => { e.preventDefault(); onOpenExisting(q.id); }}>No.{q.id} {q.subcategory?.label}({statusLabel(meta, q.status)})</a></span>
                                    ))}. 같은 건이면 새로 등록하지 말고 그 문의에 이어서 적어주세요.</div>
                            )}
                        </div>
                    </div>
                    <div className="grid2" style={{ marginBottom: 14 }}>
                        <div className="fld"><label htmlFor="inq-n-name">상호명 <span className="req">*</span></label><input id="inq-n-name" className="inp" value={f.name} onChange={(e) => set({ name: e.target.value })} /></div>
                        <div className="fld"><label htmlFor="inq-n-phone">연락처 <span style={{ color: "var(--tx3)", fontWeight: 400 }}>(이번 통화 번호로 바꿔도 됩니다)</span></label><input id="inq-n-phone" className="inp" value={f.phone} onChange={(e) => set({ phone: e.target.value })} /></div>
                    </div>
                    <div className="fld" style={{ marginBottom: 12 }}><label>구분 <span className="req">*</span></label>
                        <div className="pills">{cats.map((c) => (
                            <button key={c.code} type="button" className={`pill ${f.category === c.code ? "on" : ""}`} onClick={() => set({ category: c.code, sub: c.subcategories?.[0]?.code || "" })}>{c.label}</button>
                        ))}</div></div>
                    <div className="grid2" style={{ marginBottom: 14 }}>
                        <div className="fld"><label htmlFor="inq-n-sub">세부 항목</label><select id="inq-n-sub" className="sel" value={f.sub} onChange={(e) => set({ sub: e.target.value })}>
                            {(cat?.subcategories || []).map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}</select></div>
                        <div className="fld"><label htmlFor="inq-n-status">진행상태</label><select id="inq-n-status" className="sel" value={f.status} onChange={(e) => set({ status: e.target.value })}>
                            {(meta?.statuses || []).filter((s) => s.open).map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}</select></div>
                    </div>
                    {f.status === "on_hold" && (
                        <div className="fld" style={{ marginBottom: 14 }}><label htmlFor="inq-n-reason">보류 사유 <span style={{ color: "var(--tx3)", fontWeight: 400 }}>선택 · 200자</span></label>
                            <textarea id="inq-n-reason" className="ta" style={{ minHeight: 56 }} maxLength={200} aria-label="보류 사유" placeholder="예: 사장님 부재 · 다음 주 방문 예정" value={reason} onChange={(e) => setReason(e.target.value)} />
                            <div className="note" style={{ margin: "6px 0 0" }}><i className="fa-solid fa-mobile-screen"></i> 이 사유는 <b>가맹점주 앱에 그대로 보입니다</b>.</div></div>
                    )}
                    <div className="fld" style={{ marginBottom: 14 }}><label htmlFor="inq-n-req">요청 내용 <span className="req">*</span></label>
                        <textarea id="inq-n-req" className="ta" placeholder="가맹점주가 말한 내용을 그대로 적어주세요" value={f.request} onChange={(e) => set({ request: e.target.value })} /></div>
                    <div className="grid2" style={{ marginBottom: 14 }}>
                        <div className="fld"><label htmlFor="inq-n-assignee">담당자 <span style={{ color: "var(--tx3)", fontWeight: 400 }}>한 명만 지정</span></label>
                            <div className="row">
                                <select id="inq-n-assignee" className="sel" aria-label="담당자" style={{ flex: 1, minWidth: 0 }} value={assignees[0] ?? ""}
                                    onChange={(e) => setAssignees(e.target.value === "" ? [] : [Number(e.target.value)])}>
                                    <option value="">미지정</option>
                                    {selectable.map((g) => (
                                        <optgroup key={g.team} label={g.team}>{g.members.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</optgroup>
                                    ))}
                                </select>
                                {Number.isInteger(adminId) && assignees[0] !== adminId && (
                                    <button className="btn btn-sm" type="button" style={{ height: 32, flex: "none" }} onClick={() => setAssignees([adminId])}>내가 맡기</button>
                                )}
                            </div>
                            {!assignees.length && <div className="note" style={{ margin: "8px 0 0" }}>비워두면 「미지정」으로 등록됩니다</div>}</div>
                        <div className="fld"><label>방문 일정 <span style={{ color: "var(--tx3)", fontWeight: 400 }}>정하면 가맹점주 앱에 "방문 예정"으로 보입니다</span></label>
                            <div className="row">
                                <input type="date" className="inp" aria-label="방문 날짜" value={f.date} onChange={(e) => set({ date: e.target.value })} style={{ flex: 1.4, minWidth: 0 }} />
                                <input type="time" className="inp" aria-label="방문 시간" value={f.time} onChange={(e) => set({ time: e.target.value })} style={{ flex: 1, minWidth: 0 }} />
                            </div></div>
                    </div>
                    <div className="fld"><label>사진 첨부 <span style={{ color: "var(--tx3)", fontWeight: 400 }}>최대 {maxFiles}장</span></label>
                        <div className="imgs">
                            {previews.map((src, i) => <div key={i} className="img"><img src={src} alt="" /></div>)}
                            {files.length < maxFiles && <button className="img-add" type="button" onClick={() => fileRef.current?.click()}><span><i className="fa-solid fa-plus" style={{ fontSize: 18 }}></i><br />사진 추가</span></button>}
                        </div>
                        <input ref={fileRef} type="file" accept="image/*" multiple hidden
                            onChange={(e) => { const picked = Array.from(e.target.files); e.target.value = ""; setFiles((p) => [...p, ...picked].slice(0, maxFiles)); }} />
                    </div>
                    <div className={`err ${err ? "on" : ""}`}>{err}</div>
                </div>
                <div className="mf"><button className="btn" onClick={onClose}>취소</button>
                    <button className="btn btn-pri" onClick={submit} disabled={busy}><i className="fa-solid fa-check"></i>등록</button></div>
            </div>
        </div>
    );
}
